import { useCallback } from 'react'
import { useSwipeable } from 'react-swipeable'

import { useOnKeydown } from '@/utils/useOnKeydown'

interface Slide {
  id: string
}

type SlideNavigationProps = {
  slides: Slide[]
  onChangeSlide: (slide: Slide, index: number) => unknown
  currentSlideIndex?: number
  currentSlideId?: string
} & (
  { currentSlideIndex: number } | { currentSlideId: string }
)

export function useSlideshowNavigation(
  { slides, currentSlideIndex, currentSlideId, onChangeSlide }: SlideNavigationProps,
) {
  const slideIndex = currentSlideIndex ??
    (i => i === -1 ? 0 : i)(slides.findIndex(slide => slide.id === currentSlideId))

  const getBoundedSlideIndex = useCallback(
    function getSlideForIndex(index: number) {
      return Math.min(Math.max(index, 0), slides.length - 1)
    },
    [slides.length],
  )

  const changeSlide = useCallback((indexDelta: number) => {
    const nextIndex = getBoundedSlideIndex(slideIndex + indexDelta)
    onChangeSlide(slides[nextIndex], nextIndex)
  }, [slides, slideIndex, onChangeSlide, getBoundedSlideIndex])

  useOnKeydown({
    ArrowLeft: () => changeSlide(-1),
    ArrowRight: () => changeSlide(1),
    ArrowUp: () => changeSlide(-1),
    ArrowDown: () => changeSlide(1),
    PageUp: () => changeSlide(-1),
    PageDown: () => changeSlide(1),
    f: () => document.getElementById('formation-instructions-viewer')?.click(),
  })

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => changeSlide(1),
    onSwipedRight: () => changeSlide(-1),
  })

  return {
    swipeHandlers, slideIndex, getBoundedSlideIndex,
  }
}
